import { useState } from "react";
import { ShieldQuestion, Check, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/format";

const CATEGORY_LABEL = {
  warranty: "Extended warranty",
  protection: "Protection package",
  accessory: "Accessory",
  insurance: "Insurance",
  other: "Add-on",
};

// Gating step: before the report, confirm which optional warranties / protections the buyer actually
// asked for. Requested ones fold into the price (no warning); the rest are flagged as unwanted add-ons.
export const AddonReviewModal = ({ addons, onComplete }) => {
  const [decisions, setDecisions] = useState(() => addons.map(() => null));
  const set = (i, val) => setDecisions((d) => d.map((x, idx) => (idx === i ? val : x)));
  const answered = decisions.filter((d) => d !== null).length;
  const allAnswered = answered === addons.length;

  return (
    <div
      data-testid="addon-review-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/90 p-4 backdrop-blur-md sm:items-center sm:p-6"
    >
      <div className="animate-fade-up my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]">
        {/* Header */}
        <div className="relative border-b border-border bg-gradient-to-b from-secondary/80 to-card px-6 py-7 sm:px-9 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-warning/40 bg-warning-soft px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-warning" aria-hidden="true" />
            <span className="font-mono-plex text-[11px] font-600 uppercase tracking-[0.2em] text-warning">
              One quick step before your report
            </span>
          </div>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-warning-soft ring-1 ring-warning/30">
              <ShieldQuestion className="h-7 w-7 text-warning" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-900 leading-tight tracking-tight text-foreground sm:text-3xl">
                Which extras did you actually ask for?
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                We found {addons.length} optional {addons.length === 1 ? "item" : "items"} on this contract that
                add to the vehicle price. Tell us which you requested so we don't wrongly flag something you wanted —
                or miss something you didn't.
              </p>
            </div>
          </div>

          {/* Legend: what Yes / No does */}
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-2.5 rounded-xl border border-success/30 bg-success-soft p-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              <p className="text-xs leading-relaxed text-foreground">
                <span className="font-700 text-success">Yes, I asked for it</span> → added to your price as a
                legitimate extra. No fee warning.
              </p>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive-soft p-3">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              <p className="text-xs leading-relaxed text-foreground">
                <span className="font-700 text-destructive">No, I didn't</span> → flagged as an add-on you can ask
                the dealer to remove.
              </p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="px-6 py-6 sm:px-9">
          <ul className="space-y-4">
            {addons.map((a, i) => {
              const val = decisions[i];
              return (
                <li
                  key={`${a.label}-${i}`}
                  data-testid={`addon-review-item-${i}`}
                  className={`rounded-xl border-2 p-5 transition-colors duration-200 ${
                    val === true
                      ? "border-success/60 bg-success-soft"
                      : val === false
                      ? "border-destructive/60 bg-destructive-soft"
                      : "border-border bg-background"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span className="rounded-full border border-warning/30 bg-warning-soft px-2.5 py-0.5 font-mono-plex text-[10px] font-600 uppercase tracking-[0.12em] text-warning">
                        {CATEGORY_LABEL[a.category] || "Add-on"}
                      </span>
                      <div className="mt-2 text-lg font-700 text-foreground">{a.label}</div>
                    </div>
                    <span className="font-mono-plex text-xl font-800 text-foreground">{fmtMoney(a.amount)}</span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid={`addon-yes-${i}`}
                      onClick={() => set(i, true)}
                      className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-700 transition-colors ${
                        val === true
                          ? "border-success bg-success text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.6)]"
                          : "border-border bg-secondary text-muted-foreground hover:border-success/60 hover:text-foreground"
                      }`}
                    >
                      <Check className="h-4.5 w-4.5" /> Yes, I asked for it
                    </button>
                    <button
                      type="button"
                      data-testid={`addon-no-${i}`}
                      onClick={() => set(i, false)}
                      className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-700 transition-colors ${
                        val === false
                          ? "border-destructive bg-destructive text-white shadow-[0_4px_14px_-4px_rgba(220,38,38,0.6)]"
                          : "border-border bg-secondary text-muted-foreground hover:border-destructive/60 hover:text-foreground"
                      }`}
                    >
                      <X className="h-4.5 w-4.5" /> No, I didn't
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex flex-col items-center gap-3 border-t border-border bg-card/95 px-6 py-5 backdrop-blur sm:flex-row sm:justify-between sm:px-9">
          <span data-testid="addon-review-progress" className="font-mono-plex text-xs uppercase tracking-[0.15em] text-muted-foreground">
            {answered} of {addons.length} answered
          </span>
          <Button
            data-testid="addon-review-continue"
            disabled={!allAnswered}
            onClick={() => onComplete(decisions)}
            className="w-full rounded-full px-8 py-6 text-base font-700 sm:w-auto"
          >
            {allAnswered ? "See my compliance report" : `Answer ${addons.length - answered} more to continue`}
            {allAnswered && <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
